/**
 * useAiUsage — Plan 05-08b.
 *
 * TanStack Query hook for GET /api/admin/ai/usage.
 * Returns AI cost rollup data for the admin panel.
 *
 * T-05-08b-ADMIN-LEAK-CACHE: query is disabled when session role !== 'admin'.
 * No fetch is fired for non-admin sessions.
 */
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../auth/useSession';

export interface AiUsageRollup {
  window: { since: string; until: string };
  by_member: Array<{
    member_id: string;
    name: string;
    role: 'admin' | 'teen' | 'child';
    turns: number;
    cost_usd: number;
    daily_limit: number;
    today_used: number;
  }>;
  totals: {
    turns: number;
    cost_usd: number;
    by_model: Array<{ model: string; turns: number; cost_usd: number }>;
  };
}

export function useAiUsage(opts?: { since?: string; until?: string }) {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';

  const params = new URLSearchParams();
  if (opts?.since) params.set('since', opts.since);
  if (opts?.until) params.set('until', opts.until);
  const paramStr = params.toString();

  return useQuery<AiUsageRollup>({
    queryKey: ['admin', 'ai', 'usage', paramStr],
    enabled: isAdmin,
    queryFn: async () => {
      const url = `/api/admin/ai/usage${paramStr ? `?${paramStr}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as AiUsageRollup;
    },
  });
}
